import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { QuickViewModalShellComponent } from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { AuthService } from '../../../core/auth/auth.service';
import { AppointmentsService } from '../data-access/appointments.service';
import { AppointmentClientView, AppointmentDetail } from '../data-access/appointments.models';

@Component({
  selector: 'app-appointment-quick-view-modal',
  standalone: true,
  imports: [QuickViewModalShellComponent, TranslatePipe],
  template: `
    <lib-quick-view-modal-shell
      [isOpen]="isOpen && !!appointmentId"
      [title]="title"
      [subtitle]="subtitle | t"
      [isLoading]="isLoading"
      [loadingText]="'Loading appointment...' | t"
      [errorText]="errorText ? (errorText | t) : null"
      width="900px"
      (close)="close.emit()"
    >
      @if (clientMode && clientView) {
        <div class="quick-view-grid">
          <div class="quick-view-field">
            <span>{{ 'Procedure' | t }}</span>
            <strong>{{ clientProcedureLabel }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Clinic' | t }}</span>
            <strong>{{ clientClinicLabel }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Specialist' | t }}</span>
            <strong>{{ clientSpecialistLabel }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Session Time' | t }}</span>
            <strong>{{ clientSessionTimeLabel }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'common.status' | t }}</span>
            <strong>{{ clientStatusLabel }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Slots' | t }}</span>
            <strong>{{ clientSlotCountLabel }}</strong>
          </div>
        </div>

        @if (clientActionMessage) {
          <div class="quick-view-action-message">{{ clientActionMessage | t }}</div>
        }

        @if (clientView.actions.lockedReason) {
          <div class="quick-view-action-message quick-view-action-message--muted">
            {{ clientView.actions.lockedReason }}
          </div>
        }

        <div class="quick-view-actions">
          <button
            type="button"
            class="quick-view-action quick-view-action--primary"
            [disabled]="isActionBusy || !clientView.actions.canConfirm"
            (click)="confirmClientAppointment()"
          >
            {{ 'Confirm' | t }}
          </button>
          <button
            type="button"
            class="quick-view-action quick-view-action--danger"
            [disabled]="isActionBusy || !clientView.actions.canCancel"
            (click)="cancelClientAppointment()"
          >
            {{ 'Cancel' | t }}
          </button>
          <button
            type="button"
            class="quick-view-action"
            [disabled]="!clientView.actions.canEdit"
            (click)="editRequested.emit(clientView.id)"
          >
            {{ 'Edit' | t }}
          </button>
        </div>
      } @else if (appointment) {
        <div class="quick-view-grid">
          <div class="quick-view-field">
            <span>{{ 'Appointment' | t }}</span>
            <strong>{{ readableId(appointment.id) }}</strong>
            <div class="quick-view-id-meta">{{ appointment.id }}</div>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Client' | t }}</span>
            <strong>{{ clientLabel }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Clinic' | t }}</span>
            <strong>{{ clinicLabel }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Procedure' | t }}</span>
            <strong>{{ procedureLabel }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Device' | t }}</span>
            <strong>{{ deviceLabel }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Specialist' | t }}</span>
            <strong>{{ specialistLabel }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Session Time' | t }}</span>
            <strong>{{ sessionTimeLabel }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'common.status' | t }}</span>
            <strong>{{ appointment.status }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Confirmation Required' | t }}</span>
            <strong>{{ appointment.confirmationRequired ? ('common.yes' | t) : ('common.no' | t) }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Created' | t }}</span>
            <strong>{{ formatDateTime(appointment.createdAt ?? null) }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Updated' | t }}</span>
            <strong>{{ formatDateTime(appointment.updatedAt ?? null) }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Slots' | t }}</span>
            <strong>{{ slotCountLabel }}</strong>
          </div>
        </div>
      }
    </lib-quick-view-modal-shell>
  `,
  styles: [`
    .quick-view-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 20px;
    }

    .quick-view-action {
      border: 1px solid #cbd5e1;
      border-radius: 7px;
      background: #fff;
      color: #172033;
      cursor: pointer;
      font: inherit;
      font-weight: 700;
      min-height: 42px;
      padding: 0 16px;
    }

    .quick-view-action:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    .quick-view-action--primary {
      border-color: #2563eb;
      background: #2563eb;
      color: #fff;
    }

    .quick-view-action--danger {
      border-color: #fecaca;
      color: #b91c1c;
    }

    .quick-view-action-message {
      margin-top: 16px;
      border: 1px solid #bfdbfe;
      border-radius: 7px;
      background: #eff6ff;
      color: #1e3a8a;
      padding: 12px 14px;
    }

    .quick-view-action-message--muted {
      border-color: #e2e8f0;
      background: #f8fafc;
      color: #475569;
    }
  `],
})
export class AppointmentQuickViewModalComponent implements OnChanges {
  private readonly appointmentsService = inject(AppointmentsService);
  private readonly authService = inject(AuthService);

  @Input() public isOpen = false;
  @Input() public appointmentId: string | null = null;
  @Input() public clientViewMode = false;
  @Input() public title = 'Appointment Quick View';
  @Input() public subtitle = 'Operational appointment context without leaving the current workflow.';

  @Output() public readonly close = new EventEmitter<void>();
  @Output() public readonly editRequested = new EventEmitter<string>();

  protected appointment: AppointmentDetail | null = null;
  protected clientView: AppointmentClientView | null = null;
  protected isLoading = false;
  protected isActionBusy = false;
  protected errorText: string | null = null;
  protected clientActionMessage: string | null = null;
  protected clientLabel = '—';
  protected clinicLabel = '—';
  protected procedureLabel = '—';
  protected deviceLabel = '—';
  protected specialistLabel = '—';
  protected sessionTimeLabel = '—';
  protected slotCountLabel = '—';
  protected clientClinicLabel = '—';
  protected clientProcedureLabel = '—';
  protected clientSpecialistLabel = '—';
  protected clientSessionTimeLabel = '—';
  protected clientStatusLabel = '—';
  protected clientSlotCountLabel = '—';

  protected get clientMode(): boolean {
    return this.clientViewMode || this.authService.getCurrentRole() === 'client';
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('isOpen' in changes || 'appointmentId' in changes || 'clientViewMode' in changes) {
      const normalized = this.appointmentId?.trim() ?? '';
      if (this.isOpen && normalized) {
        this.load(normalized);
      }
    }
  }

  protected readableId(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (normalized.length > 18) {
      return `${normalized.slice(0, 8)}...${normalized.slice(-6)}`;
    }

    return normalized;
  }

  protected formatDateTime(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    const local = this.formatLocalDateTime(normalized);
    if (local) {
      return local;
    }

    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) {
      return normalized;
    }

    const [, year, month, day, hours, minutes] = match;
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  protected formatTime(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '';
    }

    const local = this.formatLocalTime(normalized);
    if (local) {
      return local;
    }

    const match = normalized.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}[T ]([0-9]{2}):([0-9]{2})/);
    if (!match) {
      return '';
    }

    const [, hours, minutes] = match;
    return `${hours}:${minutes}`;
  }

  private load(appointmentId: string): void {
    this.isLoading = true;
    this.isActionBusy = false;
    this.errorText = null;
    this.clientActionMessage = null;
    this.appointment = null;
    this.clientView = null;
    this.clientLabel = '—';
    this.clinicLabel = '—';
    this.procedureLabel = '—';
    this.deviceLabel = '—';
    this.specialistLabel = '—';
    this.sessionTimeLabel = '—';
    this.slotCountLabel = '—';
    this.clientClinicLabel = '—';
    this.clientProcedureLabel = '—';
    this.clientSpecialistLabel = '—';
    this.clientSessionTimeLabel = '—';
    this.clientStatusLabel = '—';
    this.clientSlotCountLabel = '—';

    if (this.clientMode) {
      this.loadClientView(appointmentId);
      return;
    }

    this.appointmentsService.getDetailById(appointmentId).subscribe({
      next: (appointment) => {
        this.appointment = appointment;
        this.clinicLabel = appointment.clinicLabel ?? this.readableId(appointment.clinicId);
        this.procedureLabel = appointment.procedureTypeLabel ?? this.readableId(appointment.procedureTypeId);
        this.deviceLabel = appointment.deviceId
          ? (appointment.deviceLabel ?? this.readableId(appointment.deviceId))
          : '—';
        this.clientLabel = appointment.clientLabel ?? this.readableId(appointment.clientId);
        this.specialistLabel = appointment.specialistUserId
          ? (appointment.specialistLabel ?? this.readableId(appointment.specialistUserId))
          : '—';
        this.sessionTimeLabel = this.formatSessionTime(appointment);
        this.slotCountLabel = `${appointment.slotCount} slots`;
        this.isLoading = false;
      },
      error: (error: unknown) => {
        this.errorText = resolveHttpErrorMessageKey(error, {
          fallback: 'Failed to load appointment quick view',
        });
        this.isLoading = false;
      },
    });
  }

  private loadClientView(appointmentId: string): void {
    this.appointmentsService.getClientViewById(appointmentId).subscribe({
      next: (appointment) => {
        this.clientView = appointment;
        this.clientClinicLabel = appointment.clinicLabel ?? '—';
        this.clientProcedureLabel = appointment.procedureTypeLabel ?? '—';
        this.clientSpecialistLabel = appointment.specialistLabel ?? '—';
        this.clientSessionTimeLabel = this.formatClientSessionTime(appointment);
        this.clientStatusLabel = this.statusLabel(appointment.status);
        this.clientSlotCountLabel = `${appointment.slotCount} slots`;
        this.isLoading = false;
      },
      error: (error: unknown) => {
        this.errorText = resolveHttpErrorMessageKey(error, {
          fallback: 'Failed to load appointment quick view',
        });
        this.isLoading = false;
      },
    });
  }

  protected confirmClientAppointment(): void {
    if (!this.clientView || !this.clientView.actions.canConfirm || this.isActionBusy) {
      return;
    }

    const appointmentId = this.clientView.id;
    this.isActionBusy = true;
    this.clientActionMessage = null;
    this.appointmentsService.confirm(appointmentId).subscribe({
      next: () => {
        this.clientActionMessage = 'Appointment confirmed.';
        this.loadClientView(appointmentId);
        this.isActionBusy = false;
      },
      error: (error: unknown) => {
        this.clientActionMessage = resolveHttpErrorMessageKey(error, {
          fallback: 'error.appointmentActionFailed',
        });
        this.isActionBusy = false;
      },
    });
  }

  protected cancelClientAppointment(): void {
    if (!this.clientView || !this.clientView.actions.canCancel || this.isActionBusy) {
      return;
    }

    const appointmentId = this.clientView.id;
    this.isActionBusy = true;
    this.clientActionMessage = null;
    this.appointmentsService.cancel(appointmentId).subscribe({
      next: () => {
        this.clientActionMessage = 'Appointment cancelled.';
        this.loadClientView(appointmentId);
        this.isActionBusy = false;
      },
      error: (error: unknown) => {
        this.clientActionMessage = resolveHttpErrorMessageKey(error, {
          fallback: 'error.appointmentActionFailed',
        });
        this.isActionBusy = false;
      },
    });
  }

  private formatSessionTime(appointment: AppointmentDetail): string {
    const startAt =
      appointment.appointmentStartAt ??
      appointment.primarySlotStartAt ??
      appointment.primarySlot?.startTime ??
      null;
    const endAt =
      appointment.appointmentEndAt ??
      appointment.primarySlotEndAt ??
      appointment.primarySlot?.endTime ??
      null;

    if (!startAt || !endAt) {
      return this.readableId(appointment.primarySlotId);
    }

    return `${this.formatDateTime(startAt)} → ${this.formatTime(endAt)}`;
  }

  private formatClientSessionTime(appointment: AppointmentClientView): string {
    const startAt = appointment.appointmentStartAt ?? appointment.primarySlotStartAt;
    const endAt = appointment.appointmentEndAt ?? appointment.primarySlotEndAt;

    if (!startAt || !endAt) {
      return '—';
    }

    return `${this.formatDateTime(startAt)} → ${this.formatTime(endAt)}`;
  }

  private statusLabel(status: AppointmentClientView['status']): string {
    switch (status) {
      case 'pending_confirmation':
        return 'Pending confirmation';
      case 'confirmed':
        return 'Confirmed';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'reschedule_required':
        return 'Reschedule required';
      case 'no_show':
        return 'No-show';
      default:
        return status;
    }
  }

  private formatLocalDateTime(value: string): string {
    if (!/[zZ]|[+-]\d{2}:\d{2}$/.test(value)) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year} ${this.formatDateLocalTime(date)}`;
  }

  private formatLocalTime(value: string): string {
    if (!/[zZ]|[+-]\d{2}:\d{2}$/.test(value)) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return this.formatDateLocalTime(date);
  }

  private formatDateLocalTime(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }
}
